import { Alert, BodyShort, Heading } from "@navikt/ds-react";
import { useEffect } from "react";
import { reportDashboardRouteError } from "~/observability/errors";
import { ApiErrorException, ErrorType } from "~/types/errors";

export function safeHelpUrl(helpUrl: string | null | undefined): string | null {
  if (!helpUrl) return null;
  try {
    const parsed = new URL(helpUrl);
    return parsed.protocol === "https:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

export function ErrorComponent({ error }: { error: Error }) {
  useEffect(() => reportDashboardRouteError(error), [error]);

  // Handle structured ApiErrors
  if (error instanceof ApiErrorException) {
    const { type, message, status } = error.error;

    if (type === ErrorType.AUTHENTICATION_ERROR || status === 401) {
      // Reload page to re-trigger auth flow if session expired
      // Or show login message
      // For now, simple message
      return (
        <Alert variant="warning" className="m-4">
          <Heading spacing size="small" level="3">
            Du er ikke logget inn
          </Heading>
          <BodyShort>
            Din sesjon har utløpt. Vennligst last inn siden på nytt.
          </BodyShort>
        </Alert>
      );
    }

    if (status === 503) {
      const { details } = error.error;
      return (
        <Alert variant="warning" className="m-4">
          <Heading spacing size="small" level="3">
            Midlertidig problem med teamtilgang
          </Heading>
          <BodyShort>
            {message || "Kunne ikke hente teamtilgang akkurat nå."}
          </BodyShort>
          {details && <BodyShort className="mt-2">{details}</BodyShort>}
          <BodyShort className="mt-2">
            Tips: Hvis du jobber utenfor NAV-nett, sjekk at Kolide kjører og
            prøv å laste siden på nytt. Hvis NAIS API-er er nede, prøv igjen
            litt senere.
          </BodyShort>
        </Alert>
      );
    }

    if (type === ErrorType.AUTHORIZATION_ERROR || status === 403) {
      const { details, helpUrl } = error.error;
      const validatedHelpUrl = safeHelpUrl(helpUrl);
      return (
        <Alert variant="warning" className="m-4">
          <Heading spacing size="small" level="3">
            Ingen tilgang
          </Heading>
          <BodyShort>{message}</BodyShort>
          {details && <BodyShort className="mt-2">{details}</BodyShort>}
          {validatedHelpUrl && (
            <BodyShort className="mt-4">
              <a
                href={validatedHelpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Les mer om hvordan du får tilgang →
              </a>
            </BodyShort>
          )}
        </Alert>
      );
    }

    if (type === ErrorType.NOT_FOUND || status === 404) {
      return (
        <Alert variant="info" className="m-4">
          <Heading spacing size="small" level="3">
            Fant ikke ressursen
          </Heading>
          <BodyShort>{message || "Vi fant ikke det du lette etter."}</BodyShort>
        </Alert>
      );
    }

    // Default ApiError handling
    return (
      <Alert variant="error" className="m-4">
        <Heading spacing size="small" level="3">
          Noe gikk galt ({status})
        </Heading>
        <BodyShort>
          {message || "En uventet feil oppstod ved kommunikasjon med serveren."}
        </BodyShort>
        {type && (
          <BodyShort className="mt-2 text-xs font-mono">{type}</BodyShort>
        )}
      </Alert>
    );
  }

  // Handle standard errors
  return (
    <Alert variant="error" className="m-4">
      <Heading spacing size="small" level="3">
        En uventet feil oppstod
      </Heading>
      <BodyShort>{error.message || "Vennligst prøv igjen senere."}</BodyShort>
    </Alert>
  );
}
